/**
 * Dimension App Integration
 * Handles Custom Captain's dimension app with artwork aspect ratio maintenance
 * 
 * Features:
 * - Blocks ATC until artwork and dimensions are selected
 * - Maintains artwork proportions automatically
 * - Shows artwork preview with dimension scaling
 * - Integrates with existing artwork-form events
 */

console.log('Dimension App Integration script loaded');

// Fallback `height / width` ratio per product handle, used when the product has no
// `custom.maintain_proportions` metafield. Metafield always wins when it is set.
const RATIO_BY_HANDLE = {
  'boat-name-decal': '4 / 13',
};

const FIXED_DIMENSION_HANDLES = ['boat-registration-numbers'];

class DimensionAppIntegration {
  constructor() {
    this.appContainer = null;
    this.addToCartButton = null;
    this.artworkData = null;
    this.aspectRatio = 1;
    this.maintainProportions = true;
    this.heightInput = null;
    this.widthInput = null;
    this.proportionsCheckbox = null;
    this.previewContainer = null;
    this.hasArtworkSelector = false;
    this.pendingArtwork = false;
    this.isLoadingMetadata = false;
    this.dimensionLoader = null;
    this.quantitySelector = null;
    this.priceUpdateTimeout = null;
    this.priceSpan = null;
    this.productHandle = null;
    this.hasFixedDimensions = false;
    this.isSyncingDimensions = false;

    this.init();
  }

  /**
   * Initialize the integration
   */
  init() {
    console.log('DimensionAppIntegration: Initializing...');
    this.getProductHandle();
    this.hasFixedDimensions = FIXED_DIMENSION_HANDLES.includes(this.productHandle);
    if (this.hasFixedDimensions) {
      this.maintainProportions = false;
    }
    this.checkForArtworkSelector();
    this.waitForAppLoad();
    this.setupArtworkListener();
    this.setupArtworkStateListener();
    
    // Only block initially if we have artwork selector
    if (this.hasArtworkSelector) {
      this.blockAddToCartInitially();
    }
  }

  /**
   * Get product handle from URL or global variable
   */
  getProductHandle() {
    // Try to get from global variable first (set by dexter.liquid)
    if (window.shopifyProductHandle) {
      this.productHandle = window.shopifyProductHandle;
      console.log('DimensionAppIntegration: Product handle from global:', this.productHandle);
      return;
    }

    // Extract from URL
    const path = window.location.pathname;
    const searchTerm = '/products/';
    const start = path.lastIndexOf(searchTerm);
    if (start >= 0) {
      const encoded = path.slice(start + searchTerm.length).replace('/', '');
      this.productHandle = decodeURIComponent(encoded);
      console.log('DimensionAppIntegration: Product handle from URL:', this.productHandle);
    } else {
      console.log('DimensionAppIntegration: Could not determine product handle');
    }
  }

  /**
   * Check if page has artwork selector
   */
  checkForArtworkSelector() {
    const artworkSelectors = [
      'artwork-form',
      '.artwork-selector-wrapper',
      '.artwork-swatches',
      '[data-option*="select-"]',
      '.artwork-option'
    ];

    for (const selector of artworkSelectors) {
      if (document.querySelector(selector)) {
        this.hasArtworkSelector = true;
        console.log('DimensionAppIntegration: Artwork selector found with:', selector);
        break;
      }
    }

    if (!this.hasArtworkSelector) {
      console.log('DimensionAppIntegration: No artwork selector found on page');
    }
  }


  /**
   * Parse `custom.maintain_proportions` text: "height / width" (e.g. "2 / 3").
   * Default when missing or invalid: 2 / 3 (height 2, width 3).
   * @param {string | null | undefined} raw
   * @returns {{ heightPart: number, widthPart: number }}
   */
  #parseMaintainProportionsRatioString(raw) {
    const defaultH = 2;
    const defaultW = 3;
    const source = typeof raw === 'string' && raw.trim() ? raw.trim() : `${defaultH} / ${defaultW}`;
    const match = source.match(/(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/);
    if (!match) {
      return { heightPart: defaultH, widthPart: defaultW };
    }
    const heightPart = parseFloat(match[1]);
    const widthPart = parseFloat(match[2]);
    if (!Number.isFinite(heightPart) || !Number.isFinite(widthPart) || heightPart <= 0 || widthPart <= 0) {
      return { heightPart: defaultH, widthPart: defaultW };
    }
    return { heightPart, widthPart };
  }

  /**
   * Width ÷ height from product metafield (height / width notation in metafield).
   * Used for dimension lock when there is no artwork image ratio.
   */
  #getWidthOverHeightFromProductMaintainProportions() {
    const raw = window.theme?.dimensionIntegration?.maintainProportionsRatio
      || RATIO_BY_HANDLE[this.productHandle];
    const { heightPart, widthPart } = this.#parseMaintainProportionsRatioString(raw);
    return widthPart / heightPart;
  }
  
  /**
   * Wait for the dimension app to load using mutation observer
   */
  waitForAppLoad() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const appContainer = node.querySelector?.('.cl-po--wrapper') || 
                               (node.classList?.contains('cl-po--wrapper') ? node : null);
            
            if (appContainer && !this.appContainer) {
              this.appContainer = appContainer;
              this.setupAppIntegration();
              observer.disconnect();
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also check if app is already loaded
    const existingApp = document.querySelector('.cl-po--wrapper');
    if (existingApp && !this.appContainer) {
      this.appContainer = existingApp;
      this.setupAppIntegration();
    }
  }

  /**
   * Setup app integration once loaded
   */
  setupAppIntegration() {
    console.log('Dimension app loaded, setting up integration...');
    
    this.findInputElements();
    this.hideQuantitySelector();
    this.addOverlayToApp();
    if (!this.hasFixedDimensions) {
      this.addProportionsControl();
    }
    this.addArtworkPreview();
    this.setupInputListeners();
    this.updateButtonState();
    if (!this.hasArtworkSelector && !this.hasFixedDimensions) {
      this.aspectRatio = this.#getWidthOverHeightFromProductMaintainProportions();
    }
    if (!this.hasArtworkSelector && !this.hasFixedDimensions && this.maintainProportions && this.aspectRatio) {
      this.syncDimensionsToAspectRatio();
    }

    this.setupPreviewAspectRatio();
    
    // Initialize price span if it exists in the document (even if preview wasn't added)
    if (!this.priceSpan) {
      const priceContainer = document.querySelector('.artwork-dimension-price');
      if (priceContainer) {
        this.priceSpan = priceContainer.querySelector('span');
      }
    }
    
    // Update price initially
    this.updateDimensionPrice();

    // Flag body for CSS-driven UI changes
    document.body.classList.add('dimension-app-loaded');

    // If artwork was selected before the app initialized, process now
    if (this.pendingArtwork) {
      this.removeOverlayFromApp();
      this.showPreview();
      this.updateButtonState();
      this.pendingArtwork = false;
    }
  }

  /**
   * Find input elements in the app
   */
  findInputElements() {
    if (!this.appContainer) return;
    // Artwork products (_dimension_x/_y): x is Height, y is Width.
    // Decal products (_dimensions_x/_y): x is Width, y is Height. The two apps label their axes
    // in opposite order, so match on the exact pattern instead of falling through.
    const artworkHeight = this.appContainer.querySelector('input[name*="_dimension_x"]');

    if (artworkHeight) {
      this.heightInput = artworkHeight;
      this.widthInput = this.appContainer.querySelector('input[name*="_dimension_y"]');
    } else {
      this.widthInput = this.appContainer.querySelector('input[name*="_dimensions_x"]');
      this.heightInput = this.appContainer.querySelector('input[name*="_dimensions_y"]');
    }

    if (!this.heightInput || !this.widthInput) {
      console.warn('Could not find height/width inputs in dimension app');
    }
  }

  /**
   * Vertical centre of the drawn artwork as a fraction of the bitmap height (0-1),
   * or null when the canvas is empty or cross-origin tainted.
   */
  #getArtworkCentreY(canvas) {
    try {
      const size = 128;
      const sample = document.createElement('canvas');
      sample.width = size;
      sample.height = size;

      const context = sample.getContext('2d', { willReadFrequently: true });
      context.drawImage(canvas, 0, 0, size, size);

      const { data } = context.getImageData(0, 0, size, size);
      const pixel = (row, column) => {
        const i = (row * size + column) * 4;
        return [data[i], data[i + 1], data[i + 2], data[i + 3]];
      };

      let paintedTop = null;
      let paintedBottom = null;

      for (let row = 0; row < size; row++) {
        for (let column = 0; column < size; column++) {
          if (pixel(row, column)[3] > 10) {
            if (paintedTop === null) paintedTop = row;
            paintedBottom = row;
            break;
          }
        }
      }

      if (paintedTop === null) return null;

      const background = pixel(paintedTop + 1, Math.floor(size / 2));
      const centre = (top, bottom) => (top + bottom + 1) / 2 / size;

      // No background colour picked: the painted pixels are the artwork.
      if (background[3] <= 10) return centre(paintedTop, paintedBottom);

      let top = null;
      let bottom = null;

      for (let row = paintedTop; row <= paintedBottom; row++) {
        for (let column = 0; column < size; column++) {
          const [r, g, b, a] = pixel(row, column);
          const differs = Math.abs(r - background[0]) + Math.abs(g - background[1]) + Math.abs(b - background[2]);

          if (a > 10 && differs > 60) {
            if (top === null) top = row;
            bottom = row;
            break;
          }
        }
      }

      return top === null ? centre(paintedTop, paintedBottom) : centre(top, bottom);
    } catch (error) {
      return null;
    }
  }

  /**
   * Size the preview box to the decal's width / height ratio so the background shows the
   * real proportions. The bitmap is cropped (object-fit: cover), never stretched, so the
   * name/number itself keeps its own proportions.
   */
  setupPreviewAspectRatio() {
    if (!this.appContainer) return;

    const applyRatio = () => {
      const canvas = this.appContainer.querySelector('canvas.cl-po--canvas.lower-canvas') ||
                     document.querySelector('canvas.cl-po--canvas.lower-canvas');

      if (!canvas) return;

      // The app re-renders its inputs, so read them fresh every time.
      this.findInputElements();

      const width = parseFloat(this.widthInput?.value);
      const height = parseFloat(this.heightInput?.value);

      if (!(width > 0) || !(height > 0)) return;

      canvas.style.setProperty('aspect-ratio', `${width} / ${height}`, 'important');
      canvas.style.setProperty('object-fit', 'cover', 'important');
      canvas.style.setProperty('width', 'auto', 'important');
      canvas.style.setProperty('height', 'auto', 'important');
      // Caps the preview box in both directions (a 5 x 34 decal would otherwise run
      // metres down the page); the ratio still holds inside those limits.
      canvas.style.setProperty('max-width', 'min(100%, var(--dimension-preview-size))', 'important');
      canvas.style.setProperty('max-height', 'var(--dimension-preview-size)', 'important');

      // The app doesn't draw the artwork dead centre in its square bitmap, so crop
      // around the artwork itself rather than around the bitmap's middle.
      const rect = canvas.getBoundingClientRect();
      const scale = Math.max(rect.width / canvas.width, rect.height / canvas.height);
      const scaledHeight = canvas.height * scale;
      const artworkCentre = this.#getArtworkCentreY(canvas);
      let positionY = 0.5;

      if (artworkCentre !== null && scaledHeight - rect.height > 1) {
        const offset = (artworkCentre * scaledHeight - rect.height / 2) / (scaledHeight - rect.height);
        positionY = Math.min(1, Math.max(0, offset));
      }

      canvas.style.setProperty('object-position', `50% ${positionY * 100}%`, 'important');
    };

    // Delegated so it survives the app re-creating its inputs.
    this.appContainer.addEventListener('input', (e) => {
      if (e.target?.name?.includes('_dimension')) applyRatio();
    });

    // The app rebuilds the canvas when other options change — re-apply on rebuild.
    let pending = null;
    const observer = new MutationObserver(() => {
      clearTimeout(pending);
      pending = setTimeout(applyRatio, 100);
    });
    observer.observe(this.appContainer, { childList: true, subtree: true });

    applyRatio();
  }

  /**
   * Find and hide quantity selector
   */
  hideQuantitySelector() {
    const quantitySelector = document.querySelector('.new-product__quantity-selector');
    
    if (quantitySelector) {
      this.quantitySelector = quantitySelector;
      quantitySelector.style.display = 'none';
      quantitySelector.classList.remove('d-flex');
      console.log('DimensionAppIntegration: Quantity selector hidden');
    } else {
      console.log('DimensionAppIntegration: Quantity selector not found');
    }
  }

  /**
   * Add opacity overlay to app container
   */
  addOverlayToApp() {
    // Only add overlay if we have artwork selector
    if (!this.hasArtworkSelector) {
      console.log('DimensionAppIntegration: Skipping app overlay - no artwork selector');
      return;
    }

    if (!this.appContainer) return;
    this.appContainer.style.position = 'relative';
    this.appContainer.style.opacity = '0.6';
    this.appContainer.style.pointerEvents = 'none';
    this.appContainer.setAttribute('data-blocked', 'true');
    // Set overlay label from translations
    const overlayText = window?.theme?.strings?.overlaySelectArtwork || 'Select artwork first';
    this.appContainer.setAttribute('data-overlay-label', overlayText);
  }

  /**
   * Remove overlay from app container
   */
  removeOverlayFromApp() {
    if (!this.appContainer) return;
    this.appContainer.style.opacity = '1';
    this.appContainer.style.pointerEvents = 'auto';
    this.appContainer.removeAttribute('data-blocked');
  }

  /**
   * Add maintain proportions checkbox control
   */
  addProportionsControl() {
    const helpText = this.hasArtworkSelector
      ? (window?.theme?.strings?.maintainProportionsHelp || 'Keep artwork aspect ratio when adjusting dimensions')
      : (window?.theme?.strings?.maintainProportionsHelpDimensions || 'Lock height/width ratio when adjusting dimensions');

    this.maintainProportions = true;

    const controlHtml = `
      <div class="dimension-proportions-control mb-3" style="border-top: 1px solid #e0e0e0; padding-top: 1rem; margin-top: 1rem;">
        <div class="form-check">
          <input class="form-check-input" type="checkbox" id="maintain-proportions" checked>
          <label class="form-check-label" for="maintain-proportions">
            ${window?.theme?.strings?.maintainProportionsLabel || 'Maintain proportions'}
          </label>
        </div>
        <small class="text-muted">${helpText}</small>
      </div>
    `;

    // Insert after the dimension inputs
    const dimensionContainer = this.appContainer.querySelector('.cl-po--dimension-container');
    if (dimensionContainer) {
      dimensionContainer.insertAdjacentHTML('afterend', controlHtml);
      this.proportionsCheckbox = this.appContainer.querySelector('#maintain-proportions');
      this.setupProportionsListener();
    }
  }

  /**
   * Setup proportions checkbox listener
   */
  setupProportionsListener() {
    if (!this.proportionsCheckbox) return;

    this.proportionsCheckbox.addEventListener('change', (e) => {
      this.maintainProportions = e.target.checked;

      if (this.maintainProportions) {
        // For non-artwork products, capture current ratio from inputs
        if (!this.hasArtworkSelector) {
          this.aspectRatio = this.#getWidthOverHeightFromProductMaintainProportions();
        }

        // When maintain proportions is enabled, sync existing values
        if (this.aspectRatio) {
          this.syncDimensionsToAspectRatio();
        }
      }

      this.updatePreview();
    });
  }

  /**
   * Sync dimensions to aspect ratio when maintain proportions is enabled
   */
  syncDimensionsToAspectRatio(baseField = 'auto') {
    if (!this.heightInput || !this.widthInput || !this.aspectRatio) return;

    const height = parseFloat(this.heightInput.value);
    const width = parseFloat(this.widthInput.value);

    const useWidth = baseField === 'width';

    this.isSyncingDimensions = true;
    if (useWidth && !isNaN(width) && width > 0) {
      // Use width as base, recalculate height
      const calculatedHeight = width / this.aspectRatio;
      this.heightInput.value = calculatedHeight % 1 === 0 ? calculatedHeight.toString() : calculatedHeight.toFixed(2);
      this.heightInput.dispatchEvent(new Event('input', { bubbles: true }));
    } else if (!isNaN(height) && height > 0) {
      // Use height as base, recalculate width
      const calculatedWidth = height * this.aspectRatio;
      this.widthInput.value = calculatedWidth % 1 === 0 ? calculatedWidth.toString() : calculatedWidth.toFixed(2);
      this.widthInput.dispatchEvent(new Event('input', { bubbles: true }));
    } else if (!isNaN(width) && width > 0) {
      // Fallback: only width has a value
      const calculatedHeight = width / this.aspectRatio;
      this.heightInput.value = calculatedHeight % 1 === 0 ? calculatedHeight.toString() : calculatedHeight.toFixed(2);
      this.heightInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
    this.isSyncingDimensions = false;
  }

  /**
   * Add artwork preview container
   */
  addArtworkPreview() {
    // Only add artwork preview if we have artwork selector
    if (!this.hasArtworkSelector) {
      console.log('DimensionAppIntegration: Skipping artwork preview - no artwork selector');
      return;
    }

    const previewHtml = `
      <div class="artwork-dimension-preview" style="margin-top: 1rem; text-align: center; display: none;">
        <div class="preview-label mb-2">
          <small class="text-muted">${window?.theme?.strings?.artworkPreviewLabel || 'Artwork Preview'}</small>
        </div>
        <div class="preview-image-container" style="border: 1px solid #e0e0e0; border-radius: 4px; padding: 1rem; background: #f8f9fa; max-width: 200px; margin: 0 auto;">
          <img class="preview-image" style="max-width: 100%; height: auto; display: block;" alt="Artwork preview">
        </div>
        <div class="preview-dimensions mt-2">
          <small class="text-muted">${window?.theme?.strings?.dimensionsLabel || 'Dimensions'}: <span class="dimension-text">--</span></small>
        </div>
      </div>
      <div class="artwork-dimension-price">
        <span></span>
      </div>
    `;

    this.appContainer.insertAdjacentHTML('beforeend', previewHtml);
    this.previewContainer = this.appContainer.querySelector('.artwork-dimension-preview');
    
    // Store reference to price span (check in app container first, then document)
    const priceContainer = this.appContainer.querySelector('.artwork-dimension-price') || 
                          document.querySelector('.artwork-dimension-price');
    if (priceContainer) {
      this.priceSpan = priceContainer.querySelector('span');
    }
  }

  /**
   * Setup input change listeners
   */
  setupInputListeners() {
    if (!this.heightInput || !this.widthInput) return;

    // Height input listener
    this.heightInput.addEventListener('input', (e) => {
      if (this.isSyncingDimensions) return;

      if (this.maintainProportions && this.aspectRatio && e.target.value) {
        const height = parseFloat(e.target.value);
        if (!isNaN(height) && height > 0) {
          const calculatedWidth = height * this.aspectRatio;
          this.widthInput.value = calculatedWidth % 1 === 0 ? calculatedWidth.toString() : calculatedWidth.toFixed(2);
          this.isSyncingDimensions = true;
          this.widthInput.dispatchEvent(new Event('input', { bubbles: true }));
          this.isSyncingDimensions = false;
        }
      }
      this.updateButtonState();
      this.updatePreview();
      this.debouncedUpdatePrice();
    });

    // Width input listener
    this.widthInput.addEventListener('input', (e) => {
      if (this.isSyncingDimensions) return;

      if (this.maintainProportions && this.aspectRatio && e.target.value) {
        const width = parseFloat(e.target.value);
        if (!isNaN(width) && width > 0) {
          const calculatedHeight = width / this.aspectRatio;
          this.heightInput.value = calculatedHeight % 1 === 0 ? calculatedHeight.toString() : calculatedHeight.toFixed(2);
          this.isSyncingDimensions = true;
          this.heightInput.dispatchEvent(new Event('input', { bubbles: true }));
          this.isSyncingDimensions = false;
        }
      }
      this.updateButtonState();
      this.updatePreview();
      this.debouncedUpdatePrice();
    });
  }

  /**
   * Debounced price update - waits 0.5 seconds after input stops
   */
  debouncedUpdatePrice() {
    // Clear existing timeout
    if (this.priceUpdateTimeout) {
      clearTimeout(this.priceUpdateTimeout);
    }

    // Set new timeout
    this.priceUpdateTimeout = setTimeout(() => {
      this.updateDimensionPrice();
    }, 500);
  }

  /**
   * Update artwork dimension price from product price element
   */
  updateDimensionPrice() {
    // Find the price element
    const priceElement = document.querySelector('.new-product__price');
    
    if (!priceElement) {
      console.log('DimensionAppIntegration: Price element not found');
      return;
    }

    // Get the price text (prefer data-live-format attribute, fallback to textContent)
    const priceText = priceElement.getAttribute('data-live-format') || priceElement.textContent?.trim() || '';

    if (!priceText) {
      console.log('DimensionAppIntegration: No price text found');
      return;
    }

    // Update the price span if it exists
    if (this.priceSpan) {
      this.priceSpan.textContent = priceText;
      console.log('DimensionAppIntegration: Price updated to', priceText);
    } else {
      // Try to find it again in case it wasn't initialized
      const priceContainer = this.appContainer?.querySelector('.artwork-dimension-price') || 
                            document.querySelector('.artwork-dimension-price');
      if (priceContainer) {
        this.priceSpan = priceContainer.querySelector('span');
        if (this.priceSpan) {
          this.priceSpan.textContent = priceText;
          console.log('DimensionAppIntegration: Price updated to', priceText);
        } else {
          console.log('DimensionAppIntegration: Price container found but span not found');
        }
      } else {
        console.log('DimensionAppIntegration: Price container not found');
      }
    }
  }

  /**
   * Setup artwork selection listener
   */
  setupArtworkListener() {
    document.addEventListener('artwork:selected', async (e) => {
      console.log('Artwork selected:', e.detail);
      
      this.artworkData = e.detail;
      
      // Fetch metadata to get actual artwork dimensions
      await this.fetchArtworkMetadata();
      
      if (this.appContainer) {
        this.removeOverlayFromApp();
        this.showPreview();
        this.updateButtonState();
      } else {
        // App not ready yet; handle after init
        this.pendingArtwork = true;
      }
    });
  }

  /**
   * Listen for artwork state changes to handle clearing/removal
   */
  setupArtworkStateListener() {
    document.addEventListener('artwork:statechange', (e) => {
      const state = e.detail || {};
      // When artwork input is empty, or no artwork found/selected, treat as cleared
      const artworkCleared = !state.hasInput || !state.artworkFound || !state.allArtworkSelected;

      if (artworkCleared) {
        this.artworkData = null;

        // Re-apply overlay and hide preview if app is present
        if (this.appContainer) {
          this.addOverlayToApp();
          if (this.previewContainer) {
            this.previewContainer.style.display = 'none';
          }
        }

        // Ensure button is re-blocked
        this.updateButtonState();
      }
    });
  }

  /**
   * Fetch artwork metadata from OMS API
   */
  async fetchArtworkMetadata() {
    // Block inputs and show loader
    this.blockDimensionInputs();
    
    try {
      // Get metadata URL from artwork data
      const metadataUrl = this.artworkData?.metadataUrl;
      
      if (!metadataUrl) {
        console.warn('No metadata URL found in artwork data');
        this.unblockDimensionInputs();
        return;
      }
      
      // Extract high-quality image URL from metadata URL
      // Format: https://oms.thecustomcaptain.com/trimmed-media/{id}/meta -> {id}
      const highQualityImageUrl = metadataUrl.replace('/meta', '');
      this.artworkData.highQualityImageUrl = highQualityImageUrl;
      console.log('High-quality image URL:', highQualityImageUrl);
      
      console.log('Fetching artwork metadata from:', metadataUrl);
      
      const response = await fetch(metadataUrl, {
        method: 'GET',
        mode: 'cors',
        cache: 'default',
        credentials: 'omit'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch metadata: ${response.status} ${response.statusText}`);
      }
      
      const metadata = await response.json();
      console.log('Artwork metadata received:', metadata);
      
      // Check if metadata contains an image URL (prefer metadata response if available)
      if (metadata.imageUrl || metadata.url) {
        this.artworkData.highQualityImageUrl = metadata.imageUrl || metadata.url;
        console.log('Using image URL from metadata:', this.artworkData.highQualityImageUrl);
      }
      
      // Calculate aspect ratio from actual dimensions
      if (metadata.width && metadata.height) {
        this.aspectRatio = metadata.width / metadata.height;
        console.log('Calculated aspect ratio from metadata:', this.aspectRatio, `(${metadata.width}x${metadata.height})`);
        
        // Store original dimensions for reference
        this.artworkData.originalWidth = metadata.width;
        this.artworkData.originalHeight = metadata.height;
        
        // If maintain proportions is enabled, sync existing values to new ratio
        if (this.maintainProportions && this.aspectRatio) {
          this.syncDimensionsToAspectRatio();
        }
        
        // Update the preview image with high-quality URL and aspect ratio
        this.updatePreviewImage();
        this.updatePreview();
      }
      
    } catch (error) {
      console.error('Error fetching artwork metadata:', error);
      
      // Try alternative method: load the actual image to get dimensions
      console.log('Attempting to load image directly to get dimensions...');
      const imageLoaded = await this.loadImageForDimensions();
      
      if (!imageLoaded) {
        // Fallback to default aspect ratio if available
        if (this.artworkData?.artworkAspectRatio) {
          this.aspectRatio = parseFloat(this.artworkData.artworkAspectRatio);
          console.log('Using fallback aspect ratio:', this.aspectRatio);
          // Update preview with fallback ratio
          this.updatePreviewImage();
          this.updatePreview();
        }
      }
    } finally {
      // Unblock inputs
      this.unblockDimensionInputs();
    }
  }

  /**
   * Load image directly to get natural dimensions (CORS fallback)
   */
  async loadImageForDimensions() {
    return new Promise((resolve) => {
      // Use high-quality image URL if available, otherwise extract from metadata URL
      const imageUrl = this.artworkData?.highQualityImageUrl || 
                      (this.artworkData?.metadataUrl?.replace('/meta', ''));
      
      if (!imageUrl) {
        resolve(false);
        return;
      }
      
      console.log('Loading image from:', imageUrl);
      
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          this.aspectRatio = img.naturalWidth / img.naturalHeight;
          console.log('Calculated aspect ratio from image:', this.aspectRatio, `(${img.naturalWidth}x${img.naturalHeight})`);
          
          // Store original dimensions
          this.artworkData.originalWidth = img.naturalWidth;
          this.artworkData.originalHeight = img.naturalHeight;
          
          // Store high-quality image URL if not already set
          if (!this.artworkData.highQualityImageUrl) {
            this.artworkData.highQualityImageUrl = imageUrl;
          }
          
          // Sync dimensions if needed
          if (this.maintainProportions && this.aspectRatio) {
            this.syncDimensionsToAspectRatio();
          }
          
          // Update preview image and aspect ratio
          this.updatePreviewImage();
          this.updatePreview();
          resolve(true);
        } else {
          console.warn('Image loaded but has no dimensions');
          resolve(false);
        }
      };
      
      img.onerror = (err) => {
        console.error('Failed to load image for dimensions:', err);
        resolve(false);
      };
      
      img.src = imageUrl;
    });
  }

  /**
   * Update aspect ratio from artwork data
   */
  updateAspectRatio() {
    if (this.artworkData?.artworkAspectRatio) {
      this.aspectRatio = parseFloat(this.artworkData.artworkAspectRatio);
      console.log('Updated aspect ratio:', this.aspectRatio);
      
      // If maintain proportions is enabled, sync existing values to new ratio
      if (this.maintainProportions && this.aspectRatio) {
        this.syncDimensionsToAspectRatio();
      }
    }
  }

  /**
   * Show artwork preview
   */
  showPreview() {
    if (!this.previewContainer || !this.artworkData) return;

    // Use high-quality image URL if available, otherwise fallback to thumbnail
    const imageUrl = this.artworkData.highQualityImageUrl || this.artworkData.imageUrl;
    
    if (!imageUrl) {
      console.warn('No image URL available for preview');
      return;
    }

    const previewImage = this.previewContainer.querySelector('.preview-image');
    previewImage.src = imageUrl;
    previewImage.alt = `Artwork: ${this.artworkData.artworkId || ''}`;
    
    this.previewContainer.style.display = 'block';
    this.updatePreview();
  }

  /**
   * Update preview image with high-quality URL
   */
  updatePreviewImage() {
    if (!this.previewContainer || !this.artworkData?.highQualityImageUrl) return;

    const previewImage = this.previewContainer.querySelector('.preview-image');
    if (previewImage && previewImage.src !== this.artworkData.highQualityImageUrl) {
      previewImage.src = this.artworkData.highQualityImageUrl;
      console.log('DimensionAppIntegration: Updated preview image to high-quality URL');
    }
  }

  /**
   * Update preview based on current dimensions
   */
  updatePreview() {
    if (!this.previewContainer) return;

    const height = parseFloat(this.heightInput?.value) || 0;
    const width = parseFloat(this.widthInput?.value) || 0;
    const previewImage = this.previewContainer.querySelector('.preview-image');
    const dimensionText = this.previewContainer.querySelector('.dimension-text');

    console.log('DimensionAppIntegration: updatePreview called - aspectRatio:', this.aspectRatio, 'height:', height, 'width:', width, 'maintainProportions:', this.maintainProportions);

    // Update dimension text
    if (dimensionText) {
      if (height > 0 && width > 0) {
        dimensionText.textContent = `${height}" × ${width}"`;
      } else {
        dimensionText.textContent = '--';
      }
    }

    // Always update image aspect ratio when we have a valid aspect ratio
    if (previewImage && this.aspectRatio) {
      if (this.maintainProportions) {
        // Maintain original aspect ratio from metadata
        previewImage.style.aspectRatio = this.aspectRatio.toString();
        previewImage.style.objectFit = 'fill';
        console.log('DimensionAppIntegration: Set preview aspect ratio to:', this.aspectRatio);
      } else if (height > 0 && width > 0) {
        // Stretch to match input dimensions only if we have both values
        const inputRatio = width / height;
        previewImage.style.aspectRatio = inputRatio.toString();
        previewImage.style.objectFit = 'fill';
        console.log('DimensionAppIntegration: Set preview to input ratio:', inputRatio);
      }
    }
  }

  /**
   * Block add to cart button initially
   */
  blockAddToCartInitially() {
    this.findAddToCartButton();
    if (this.addToCartButton) {
      this.blockAddToCartButton();
    } else {
      // Retry finding button after a short delay
      setTimeout(() => {
        this.findAddToCartButton();
        if (this.addToCartButton) {
          this.blockAddToCartButton();
        }
      }, 1000);
    }
  }

  /**
   * Find add to cart button
   */
  findAddToCartButton() {
    // Try multiple selectors for ATC button
    const selectors = [
      '.new-product__add-to-cart-btn--app',
      'button[name="add"]',
      '.btn-product-form',
      '.product-form__cart-submit',
      '[data-add-to-cart]',
      'button[type="submit"][form*="product-form"]',
      '.new-product__add-to-cart',
      '.product-form button[type="submit"]',
      'form[action*="/cart/add"] button[type="submit"]'
    ];

    for (const selector of selectors) {
      const button = document.querySelector(selector);
      if (button && (button.textContent || '').toLowerCase().includes('cart')) {
        console.log('DimensionAppIntegration: Found ATC button with selector:', selector);
        this.addToCartButton = button;
        break;
      }
    }
    
    if (!this.addToCartButton) {
      console.warn('DimensionAppIntegration: Could not find add to cart button');
    }
  }

  /**
   * Block add to cart button
   */
  blockAddToCartButton() {
    if (!this.addToCartButton) {
      console.log('DimensionAppIntegration: Cannot block ATC button - button not found');
      return;
    }

    this.addToCartButton.disabled = true;
    this.addToCartButton.setAttribute('data-dimension-blocked', 'true');
    
    // Store original text and update
    if (!this.addToCartButton.getAttribute('data-original-text')) {
      this.addToCartButton.setAttribute('data-original-text', this.addToCartButton.textContent);
    }
    const txt = window?.theme?.strings?.btnSelectArtworkAndDimensions || 'Select artwork and dimensions';
    this.addToCartButton.textContent = txt;
  }

  /**
   * Unblock add to cart button
   */
  unblockAddToCartButton() {
    if (!this.addToCartButton) {
      console.log('DimensionAppIntegration: Cannot unblock ATC button - button not found');
      return;
    }

    this.addToCartButton.disabled = false;
    this.addToCartButton.removeAttribute('data-dimension-blocked');
    
    // Restore original text
    const originalText = this.addToCartButton.getAttribute('data-original-text');
    if (originalText) {
      this.addToCartButton.textContent = originalText;
    }
  }

  /**
   * Block dimension inputs while loading metadata
   */
  blockDimensionInputs() {
    if (!this.heightInput || !this.widthInput) return;
    
    this.isLoadingMetadata = true;
    this.heightInput.disabled = true;
    this.widthInput.disabled = true;
    
    // Add loading state to app container
    if (this.appContainer) {
      this.appContainer.classList.add('dimension-loading');
    }
    
    // Show loader
    this.showDimensionLoader();
  }

  /**
   * Unblock dimension inputs after metadata is loaded
   */
  unblockDimensionInputs() {
    if (!this.heightInput || !this.widthInput) return;
    
    this.isLoadingMetadata = false;
    this.heightInput.disabled = false;
    this.widthInput.disabled = false;
    
    // Remove loading state from app container
    if (this.appContainer) {
      this.appContainer.classList.remove('dimension-loading');
    }
    
    // Hide loader
    this.hideDimensionLoader();
  }

  /**
   * Show dimension loader
   */
  showDimensionLoader() {
    if (!this.appContainer) return;
    
    // Create loader if it doesn't exist
    if (!this.dimensionLoader) {
      const loaderText = window?.theme?.strings?.loadingArtworkDimensions || 'Loading artwork dimensions...';
      const loaderHtml = `
        <div class="dimension-loader" style="
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
          border-radius: 6px;
        ">
          <div style="text-align: center;">
            <div class="spinner-border text-primary mb-2" role="status" style="width: 2rem; height: 2rem;">
              <span class="visually-hidden">Loading...</span>
            </div>
            <div style="font-size: 0.875rem; color: #6c757d; font-weight: 500;">${loaderText}</div>
          </div>
        </div>
      `;
      
      // Find dimension container to append loader
      const dimensionContainer = this.appContainer.querySelector('.cl-po--dimension-container');
      if (dimensionContainer) {
        dimensionContainer.style.position = 'relative';
        dimensionContainer.insertAdjacentHTML('beforeend', loaderHtml);
        this.dimensionLoader = dimensionContainer.querySelector('.dimension-loader');
      }
    } else {
      // Show existing loader
      this.dimensionLoader.style.display = 'flex';
    }
  }

  /**
   * Hide dimension loader
   */
  hideDimensionLoader() {
    if (this.dimensionLoader) {
      this.dimensionLoader.style.display = 'none';
    }
  }

  /**
   * Update button state based on current conditions
   */
  updateButtonState() {
    // Only manage button state if we have artwork selector
    if (!this.hasArtworkSelector) {
      return;
    }

    // Try to find button if we don't have it yet
    if (!this.addToCartButton) {
      this.findAddToCartButton();
    }

    const hasArtwork = !!this.artworkData;
    const hasHeight = this.heightInput?.value && parseFloat(this.heightInput.value) > 0;
    const hasWidth = this.widthInput?.value && parseFloat(this.widthInput.value) > 0;
    const hasDimensions = hasHeight && hasWidth;

    if (hasArtwork && hasDimensions) {
      this.unblockAddToCartButton();
    } else {
      this.blockAddToCartButton();
      
      // Update button text based on what's missing (only if button exists)
      if (this.addToCartButton) {
        if (!hasArtwork) {
          this.addToCartButton.textContent = window?.theme?.strings?.btnSelectArtworkFirst || 'Select artwork first';
        } else if (!hasDimensions) {
          this.addToCartButton.textContent = window?.theme?.strings?.btnEnterDimensions || 'Enter dimensions';
        }
      }
    }
  }
}

// Initialize when DOM is ready
console.log('DimensionAppIntegration: Script execution started, DOM ready state:', document.readyState);

// Also check if the dimension app is already in the DOM
const existingApp = document.querySelector('.cl-po--wrapper');
console.log('DimensionAppIntegration: Existing app found:', !!existingApp);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    console.log('DimensionAppIntegration: DOM loaded, creating instance');
    new DimensionAppIntegration();
  });
} else {
  console.log('DimensionAppIntegration: DOM already ready, creating instance immediately');
  new DimensionAppIntegration();
}